export interface LatLng {
  lat: number;
  lng: number;
}

// Fallback map center used when no valid coordinate is available from any
// source (listing data, geolocation, IP lookup, address search). Roughly the
// geographic center of the US — a benign default that never crashes the map.
export const DEFAULT_MAP_CENTER: LatLng = { lat: 39.8283, lng: -98.5795 };

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/**
 * A lat/lng is only usable by Google Maps if both parts are finite numbers in
 * range. google-map-react passes coordinates straight into
 * fromLatLngToDivPixel, which throws InvalidValueError (and, with no error
 * boundary, white-screens the page) on NaN/undefined/out-of-range values.
 */
export const isValidLatLng = (value?: Partial<LatLng> | null): value is LatLng =>
  !!value &&
  isFiniteNumber(value.lat) &&
  isFiniteNumber(value.lng) &&
  Math.abs(value.lat) <= 90 &&
  Math.abs(value.lng) <= 180 &&
  // treat the null-island 0,0 as "no location" — it's what dirty/empty data
  // coerces to and is never a real listing location.
  !(value.lat === 0 && value.lng === 0);

/** Coerce a possibly-dirty pair (strings, null, undefined) to numbers. */
export const toLatLng = (lat: unknown, lng: unknown): LatLng | null => {
  const parsed = { lat: Number(lat), lng: Number(lng) };
  return isValidLatLng(parsed) ? parsed : null;
};

/**
 * A GeoJSON-style coordinates array is [lng, lat]. Returns a valid LatLng or
 * null. Used for listing `coordinates.coordinates`.
 */
export const fromGeoJson = (coordinates?: readonly (number | string)[] | null): LatLng | null => {
  if (!coordinates || coordinates.length < 2) return null;
  return toLatLng(coordinates[1], coordinates[0]);
};
